/**
 * Smooth scrolling for in-page anchors with header offset.
 *
 * Works with Shopify section wrappers like:
 *   <div id="shopify-section-{{ section.id }}" class="shopify-section">...</div>
 *
 * Also works for cross-page anchors like "/#shopify-section-xxxx".
 */
(function () {
  const prefersReducedMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function parseAnchorMap(raw) {
    if (!raw || typeof raw !== 'string') return {};
    const map = {};
    raw
      .split('\n')
      .map((l) => l.trim())
      .filter((l) => l && !l.startsWith('#'))
      .forEach((line) => {
        const idx = line.indexOf('=');
        if (idx === -1) return;
        const key = line.slice(0, idx).trim().replace(/^#/, '');
        const value = line.slice(idx + 1).trim().replace(/^shopify-section-/, '');
        if (!key || !value) return;
        map[key] = value;
      });
    return map;
  }

  function resolveHash(hash) {
    const h = (hash || '').trim();
    if (!h.startsWith('#')) return h ? `#${h}` : '';
    const slug = h.slice(1);
    const rawMap = window.__ANCHOR_LINK_MAP__ || '';
    const map = parseAnchorMap(rawMap);
    const mapped = map[slug];
    if (!mapped) return h;
    // We scroll to the Shopify wrapper id
    return `#shopify-section-${mapped}`;
  }

  function getHeaderOffsetPx() {
    const raw =
      getComputedStyle(document.body).getPropertyValue('--header-group-height') ||
      getComputedStyle(document.body).getPropertyValue('--header-height') ||
      '0px';
    const n = parseFloat(String(raw).trim().replace('px', ''));
    return Number.isFinite(n) ? n : 0;
  }

  function scrollToHash(hash, { replaceState = false } = {}) {
    if (!hash || hash === '#') return false;

    const resolvedHash = resolveHash(hash);
    let target = null;
    try {
      target = document.querySelector(resolvedHash);
    } catch {
      // Invalid selector (e.g. spaces) — try id lookup.
      const id = resolvedHash.startsWith('#') ? resolvedHash.slice(1) : resolvedHash;
      target = document.getElementById(id);
    }
    if (!target) return false;

    const headerOffset = getHeaderOffsetPx();
    const extraGap = 8; // small breathing room below header
    const y = Math.max(0, target.getBoundingClientRect().top + window.pageYOffset - headerOffset - extraGap);

    if (replaceState) {
      history.replaceState(null, '', hash);
    }

    window.scrollTo({
      top: y,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });

    return true;
  }

  // Intercept clicks on same-page anchors.
  document.addEventListener('click', (e) => {
    const a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
    if (!a) return;

    const href = a.getAttribute('href') || '';
    if (!href.includes('#')) return;

    // Only handle same-origin + same-path anchors (including plain "#id").
    let url;
    try {
      url = new URL(href, window.location.href);
    } catch {
      return;
    }

    const isSamePage = url.origin === window.location.origin && url.pathname === window.location.pathname;
    if (!isSamePage) return;

    if (url.hash && scrollToHash(url.hash)) {
      e.preventDefault();
      // Keep the hash in the URL (so refresh / sharing works)
      history.pushState(null, '', url.hash);
    }
  });

  // Handle initial load with hash (including when arriving from another page).
  window.addEventListener('load', () => {
    if (window.location.hash) {
      // Delay to allow header height vars + sections to render.
      setTimeout(() => scrollToHash(window.location.hash, { replaceState: true }), 50);
    }
  });

  // Handle manual hash changes (or back/forward).
  window.addEventListener('hashchange', () => {
    if (window.location.hash) scrollToHash(window.location.hash, { replaceState: true });
  });
})();

